import React from 'react';
import { defineMessages, useIntl, type MessageDescriptor } from 'react-intl';
import type { AlgoliaCompanyRecord } from '../../types/algolia-company-record';

const messages = defineMessages({
  heading: {
    id: 'companyProfile.communityInsights.heading',
    defaultMessage: 'What people say online',
    description: 'Heading of the company profile card summarizing public web discussion about the employer',
  },
  subtitle: {
    id: 'companyProfile.communityInsights.subtitle',
    defaultMessage: 'Summarized from public Reddit, Suomi24, and forum discussion',
    description: 'Subtitle under the community insights heading explaining where the summary comes from',
  },
  sentimentPositive: {
    id: 'companyProfile.communityInsights.sentimentPositive',
    defaultMessage: 'Mostly positive',
    description: 'Badge label when public discussion about the employer is mostly positive',
  },
  sentimentNegative: {
    id: 'companyProfile.communityInsights.sentimentNegative',
    defaultMessage: 'Mostly negative',
    description: 'Badge label when public discussion about the employer is mostly negative',
  },
  sentimentMixed: {
    id: 'companyProfile.communityInsights.sentimentMixed',
    defaultMessage: 'Mixed',
    description: 'Badge label when public discussion about the employer is mixed',
  },
  sentimentInsufficient: {
    id: 'companyProfile.communityInsights.sentimentInsufficient',
    defaultMessage: 'Not enough data',
    description: 'Badge label when there is not enough public discussion to summarize',
  },
  prosTitle: {
    id: 'companyProfile.communityInsights.prosTitle',
    defaultMessage: 'What people like',
    description: 'Title of the list of positive themes from public discussion about the employer',
  },
  consTitle: {
    id: 'companyProfile.communityInsights.consTitle',
    defaultMessage: 'Common complaints',
    description: 'Title of the list of negative themes from public discussion about the employer',
  },
  sourcesTitle: {
    id: 'companyProfile.communityInsights.sourcesTitle',
    defaultMessage: 'Sources',
    description: 'Title of the list of links to the public discussions the summary was based on',
  },
});

interface CompanyCommunityInsightsProps {
  company: Pick<AlgoliaCompanyRecord, 'communityInsights'>;
  className?: string;
}

function sentimentBadge(sentiment: string): { label: MessageDescriptor; classes: string } {
  switch (sentiment) {
    case 'positive':
      return {
        label: messages.sentimentPositive,
        classes: 'bg-[#3B5E45] text-white dark:bg-[#1A2E22] dark:text-[#98CFA8]',
      };
    case 'negative':
      return { label: messages.sentimentNegative, classes: 'bg-red-500 text-white dark:bg-red-900 dark:text-red-100' };
    case 'mixed':
      return { label: messages.sentimentMixed, classes: 'bg-[#6E4F00] text-white dark:bg-[#2A1E08] dark:text-[#D4A820]' };
    default:
      return {
        label: messages.sentimentInsufficient,
        classes: 'bg-[#F2EADF] text-[#5A4A3A] dark:bg-[#2A1D0F] dark:text-[#B8A48C]',
      };
  }
}

function ThemeList({ title, items }: { title: string; items: string[] }) {
  if (items.length === 0) return null;
  return (
    <div>
      <h3 className="text-xs font-semibold uppercase tracking-wide text-[#7A6E5F] dark:text-[#B8A48C]">{title}</h3>
      <ul className="mt-1.5 space-y-1">
        {items.map((item) => (
          <li key={item} className="text-sm text-[#1E1A14] dark:text-[#F2EADF]">
            {item}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function CompanyCommunityInsights({ company, className = '' }: CompanyCommunityInsightsProps) {
  const intl = useIntl();
  const insights = company.communityInsights;
  if (!insights || insights.sentiment === 'insufficient_data') {
    return null;
  }

  const badge = sentimentBadge(insights.sentiment);

  return (
    <div
      className={`rounded-2xl border border-[#DDD5C8] bg-white shadow-sm dark:border-[#4A3520] dark:bg-[#1F1509] p-4 sm:p-5 ${className}`}
    >
      <div className="mb-4 flex flex-col gap-3 min-[420px]:flex-row min-[420px]:items-start min-[420px]:justify-between">
        <div className="min-w-0 flex-1 pr-0 min-[420px]:pr-3">
          <h2 className="text-base font-semibold text-[#1E1A14] dark:text-[#F2EADF]">
            {intl.formatMessage(messages.heading)}
          </h2>
          <p className="mt-0.5 text-xs text-[#5A4A3A] dark:text-[#B8A48C]">{intl.formatMessage(messages.subtitle)}</p>
        </div>
        <div className="flex shrink-0 justify-end min-[420px]:justify-start">
          <span className={`rounded-full px-3 py-1 text-xs font-medium ${badge.classes}`}>
            {intl.formatMessage(badge.label)}
          </span>
        </div>
      </div>

      <p className="text-sm leading-relaxed text-[#1E1A14] dark:text-[#F2EADF]">{insights.summary}</p>

      <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <ThemeList title={intl.formatMessage(messages.prosTitle)} items={insights.prosThemes} />
        <ThemeList title={intl.formatMessage(messages.consTitle)} items={insights.consThemes} />
      </div>

      {insights.caveats && (
        <p className="mt-4 text-xs italic leading-relaxed text-[#7A6E5F] dark:text-[#B8A48C]">{insights.caveats}</p>
      )}

      {insights.sources.length > 0 && (
        <div className="mt-4 border-t border-[#DDD5C8] pt-4 dark:border-[#4A3520]">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-[#7A6E5F] dark:text-[#B8A48C]">
            {intl.formatMessage(messages.sourcesTitle)}
          </h3>
          <ul className="mt-1.5 space-y-1">
            {insights.sources.map((source) => (
              <li key={source.url} className="min-w-0 truncate text-sm">
                <a
                  href={source.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium text-[#3B5E45] underline decoration-[#3B5E45]/30 underline-offset-2 hover:text-[#2A4533] dark:text-[#98CFA8] dark:decoration-[#98CFA8]/40 dark:hover:text-[#B6D4BC]"
                  title={source.title}
                >
                  {source.title || source.domain}
                </a>
                <span className="ml-1.5 text-xs text-[#7A6E5F] dark:text-[#B8A48C]">({source.domain})</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

CompanyCommunityInsights.displayName = 'CompanyCommunityInsights';
